"use client";

import { format } from "date-fns";

import { cn } from "@/lib/utils";

import { TONE_ACTIVE, useCalendarContext } from "../calendar-context";
import { DISABLED_HATCH } from "../styles";
import { formatDayLabel } from "../utils";

// =============================================================================

type MonthDayCellProps = {
  day: Date;
  dayKey: string;
  isToday: boolean;
  /** Leading / trailing day of an adjacent month */
  isOutside: boolean;
  /** Fully unavailable: dimmed, hatched, not clickable */
  isDisabled: boolean;
  /** Holds the grid's single tab stop */
  isFocusTarget: boolean;
  className?: string;
};

/**
 * Day cell of the month grid. Its events are drawn by the row overlay
 * (MonthWeekLanes), keyboard navigation is handled by the grid.
 */
export function MonthDayCell({
  day,
  dayKey,
  isToday,
  isOutside,
  isDisabled,
  isFocusTarget,
  className,
}: MonthDayCellProps) {
  const { locale, onDateClick, renderDayAddon, tone } = useCalendarContext();
  const isClickable = !!onDateClick && !isDisabled;
  const addon = renderDayAddon?.(day);

  return (
    <div
      role="gridcell"
      data-day={dayKey}
      tabIndex={isFocusTarget ? 0 : -1}
      aria-current={isToday ? "date" : undefined}
      aria-disabled={isDisabled || undefined}
      onClick={isClickable ? () => onDateClick(day) : undefined}
      className={cn(
        "relative flex min-w-0 flex-col items-start p-1.5 outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-inset",
        isOutside && "bg-surface-2/40",
        isDisabled && cn("cursor-not-allowed", DISABLED_HATCH),
        isClickable && "cursor-pointer transition-colors hover:bg-surface-2",
        className
      )}
    >
      <span className="sr-only">{formatDayLabel(day, locale, true)}</span>
      <span
        aria-hidden
        className={cn(
          "grid size-6 shrink-0 place-items-center rounded-full text-xs font-medium tabular-nums",
          isToday
            ? TONE_ACTIVE[tone]
            : isOutside || isDisabled
              ? "text-muted-foreground/70"
              : "text-foreground"
        )}
      >
        {format(day, "d")}
      </span>
      {addon != null && addon !== false && (
        <div className="mt-0.5 h-4 w-full truncate text-xs leading-4 text-muted-foreground">
          {addon}
        </div>
      )}
    </div>
  );
}
